import dayjs from 'dayjs';
import type { BackofficeSale, BackofficeSalesFilters } from '@/api/services/retail-wizard/contract';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';

export const EMPTY_FILTERS: BackofficeSalesFilters = { retailerId: '' };

/** Claves que cuentan como "un filtro activo" en el contador del encabezado. */
const COUNTABLE = [
    'storeId',
    'sellerId',
    'customerId',
    'query',
    'provider',
    'type',
    'status',
    'sku',
    'centralSyncStatus',
    'centralCancelStatus',
] as const;

export function activeFilterCount(f: BackofficeSalesFilters): number {
    // `retailerId` NO cuenta: es el contexto de la pantalla, no un filtro que el operador aplicó.
    let n = COUNTABLE.filter((k) => !!f[k]).length;
    // El rango de fechas es UN filtro conceptual aunque sean dos campos — contarlo doble confunde.
    if (f.from ?? f.to) n += 1;
    if (f.minPesos !== undefined || f.maxPesos !== undefined) n += 1;
    return n;
}

/** Atajos del mock: 0 = hoy, 7 = últimos 7 días, 30 = últimos 30, -1 = limpiar. */
export function dateShortcut(days: number, now = new Date()): { from?: string; to?: string } {
    if (days < 0) return { from: undefined, to: undefined };
    const to = dayjs(now);
    return { from: to.subtract(days, 'day').format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
}

/**
 * Los filtros que viajan al API. `query` NUNCA: la búsqueda se resuelve en CLIENTE.
 *
 * El endpoint acepta `q`, pero no busca: lo traduce a `contains(searchIndex, …)`, que DynamoDB
 * evalúa DESPUÉS de leer la página. Un folio exacto que vive en la página 6 devuelve `count: 0` en
 * la 1, y las ventas anteriores al snapshot no tienen `searchIndex`, así que no matchean nunca.
 * Mandarlo además cambiaba la query key y tiraba las páginas ya cargadas al teclear.
 */
export function serverSideFilters(f: BackofficeSalesFilters): BackofficeSalesFilters {
    return { ...f, query: undefined };
}

/** Minúsculas y sin acentos: buscar "maria" tiene que encontrar a "María". */
function normalize(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
}

/** Los cuatro campos que el mock declara buscables: folio, cliente, CURP y el id del crédito. */
export type SearchableSale = Pick<
    BackofficeSale,
    'folio' | 'customerName' | 'customerCurp' | 'creditId' | 'retailCustomerId'
>;

/** `true` si alguno de los cuatro campos buscables contiene el término. */
export function matchesQuery(sale: SearchableSale, term: string): boolean {
    const needle = normalize(term.trim());
    if (needle === '') return true;
    return [sale.folio, saleCustomerLabel(sale), sale.customerCurp, sale.creditId].some(
        (field) => field !== null && normalize(field).includes(needle),
    );
}

/**
 * Filtra las ventas YA CARGADAS por el término tecleado. Sin término devuelve la misma referencia,
 * para no romper la memoización de quien la consume.
 */
export function filterByQuery<T extends SearchableSale>(sales: T[], term?: string): T[] {
    if (!term || term.trim() === '') return sales;
    return sales.filter((s) => matchesQuery(s, term));
}
